import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  MdAlternateEmail, MdOutlinePhoneIphone, MdLocationOn, MdCalendarToday, 
  MdEdit, MdCameraAlt, MdClose
} from "react-icons/md";
import { IoIosCloseCircle } from "react-icons/io";
import { FaUserCircle, FaCheck, FaTimes } from "react-icons/fa";
import { updateUser, setUpdateUser } from '../../state';
import TopBar from '../global/TopBar';
import EditUserPhoto from './EditUserPhoto';
import { motion, AnimatePresence } from 'framer-motion';

const Profile = () => {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const dispatch = useDispatch();
  const [profileImageUrl, setProfileImageUrl] = useState(user.profile_photo_url);
  const [editedUser, setEditedUser] = useState(user);
  const [isPhotoPopupOpen, setIsPhotoPopupOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({ available: true, message: '' });
  const [originalUsername, setOriginalUsername] = useState(user.username);

  useEffect(() => {
    setProfileImageUrl(user.profile_photo_url);
    setEditedUser(user);
    setOriginalUsername(user.username);
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({ ...prev, [name]: value }));
    
    // Reset username status when username is changed
    if (name === 'username' && value !== originalUsername) {
      setUsernameStatus({ available: true, message: '' });
      // Mark username as needing validation when changed
      if (value !== originalUsername) {
        setUsernameStatus({ available: false, message: '', needsValidation: true });
      }
    }
  }

  const checkUsernameAvailability = async () => {
    if (!editedUser.username || editedUser.username.trim() === '') {
      setUsernameStatus({ available: false, message: 'Username cannot be empty' });
      return;
    }

    try {
      setIsCheckingUsername(true);
      const response = await fetch(
        `${process.env.REACT_APP_USER_API_URL}/check-username?username=${encodeURIComponent(
          editedUser.username
        )}&userId=${encodeURIComponent(user.id)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined,
            'Api-Name': 'user-api',
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to check username');
      }

      const data = await response.json();
      // Remove the needsValidation flag when validated
      setUsernameStatus({...data, needsValidation: false});
    } catch (error) {
      setUsernameStatus({ available: false, message: 'Error checking username', needsValidation: false });
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    
    // Check if username is available before saving
    if (editedUser.username !== originalUsername && !usernameStatus.available) {
      setMessage({ 
        type: 'error',
        text: 'Username is already taken. Please choose another.'
      });
      return;
    }
    
    try {
      const updatedFields = {};
      Object.keys(editedUser).forEach(key => {
        if (editedUser[key] !== user[key]) {
          updatedFields[key] = editedUser[key];
        }
      });

      for (const [field, value] of Object.entries(updatedFields)) {
        const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/${user.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined,
            'Api-Name': 'user-api',
          },
          body: JSON.stringify({ field, value }),
        });

        if (!response.ok) {
          throw new Error(`Failed to update ${field}`);
        }
      }

      dispatch(setUpdateUser({...user, ...updatedFields}));
      setIsEditOpen(false);
      setOriginalUsername(editedUser.username);
      
      setMessage({ 
        type: 'success',
        text: 'Profile updated successfully!'
      });
      
      setTimeout(() => {
        setMessage(null);
      }, 3000);
      
    } catch (error) {
      setMessage({ 
        type: 'error',
        text: error.message || 'Failed to update profile. Please try again.'
      });
      
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    }
  };

  const handlePhotoChange = (newImageUrl) => {
    dispatch(updateUser({ user: { ...user, profile_photo_url: newImageUrl } }));
  };

  const ProfileField = ({ icon: Icon, label, value }) => (
    <motion.div
      className="flex items-center space-x-3 p-3 sm:p-4 bg-gradient-to-r from-soft-pink/5 to-soft-pink/10 rounded-xl backdrop-blur-md border border-pri-white/10 hover:border-soft-pink/20 transition-all duration-300 relative overflow-hidden"
      whileHover={{ scale: 1.02 }}
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/5 via-transparent to-soft-pink/10" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/20 to-transparent" />
      </div>
      
      <div className="relative bg-gradient-to-br from-soft-pink/20 to-soft-pink/10 p-2.5 rounded-lg">
        <Icon className="text-soft-pink text-xl" />
      </div>
      <div className="relative flex-1">
        <p className="text-xs font-medium text-soft-pink/70">{label}</p>
        <p className="text-sm sm:text-base text-pri-white font-medium truncate">{value || 'Not provided'}</p>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark via-sec-dark to-pri-dark">
      <TopBar />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-6xl">
        <div className="relative">
          {/* Glossy Background Effects */}
          <motion.div 
            className="absolute inset-0 w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Glass Overlay */}
            <div className="absolute inset-0 backdrop-blur-xl bg-gradient-to-b from-pri-dark/40 to-sec-dark/40" />
          </motion.div>

          {/* Main Content */}
          <div className="relative">
            <div className="bg-gradient-to-b from-sec-dark/80 to-pri-dark/80 rounded-2xl sm:rounded-3xl shadow-2xl backdrop-blur-xl border border-pri-white/10 overflow-hidden">
              {/* Glossy Effect Overlays */}
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-soft-pink/10" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/10 to-transparent" />
              </div>

              {/* Profile Header */}
              <div className="relative px-4 sm:px-6 md:px-10 py-6 sm:py-8">
                <div className="flex flex-col items-center gap-4 sm:gap-6">
                  {/* Profile Image */}
                  <motion.div 
                    className="relative group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="relative cursor-pointer" onClick={() => setIsPhotoPopupOpen(true)}>
                      {profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt={user.name}
                          className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-soft-pink/50 shadow-2xl"
                        />
                      ) : (
                        <FaUserCircle className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 text-pri-gray" />
                      )}
                      <motion.div
                        className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                      >
                        <MdCameraAlt className="text-pri-white text-2xl sm:text-3xl" />
                      </motion.div>
                    </div>
                  </motion.div>

                  {/* Profile Info */}
                  <div className="flex-1 text-center w-full">
                    <motion.h1 
                      className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-soft-pink to-pri-white bg-clip-text text-transparent mb-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {user.name}
                    </motion.h1>
                    <motion.p 
                      className="text-base sm:text-lg text-soft-pink mb-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      @{user.username || 'username'}
                    </motion.p>
                    <motion.p 
                      className="text-sm sm:text-base text-pri-gray mb-4 sm:mb-6 px-4 sm:px-0"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      {user.bio || 'No bio provided'}
                    </motion.p>
                    <motion.div 
                      className="flex flex-wrap gap-2 sm:gap-3 justify-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <motion.button
                        onClick={() => setIsPhotoPopupOpen(true)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base rounded-full bg-gradient-to-r from-soft-pink to-soft-pink/70 text-black font-semibold hover:shadow-lg transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <MdCameraAlt className="inline mr-1 sm:mr-2" /> Change Photo
                      </motion.button>
                      <motion.button
                        onClick={() => setIsEditOpen(true)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base rounded-full bg-gradient-to-r from-soft-pink to-soft-pink/70 text-black font-semibold hover:shadow-lg transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <MdEdit className="inline mr-1 sm:mr-2" /> Edit Profile
                      </motion.button>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Profile Details Grid */}
              <div className="px-4 sm:px-6 md:px-10 py-4 sm:py-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <ProfileField icon={MdAlternateEmail} label="Email" value={user.email} />
                  <ProfileField icon={MdOutlinePhoneIphone} label="Phone" value={user.mobile} />
                  <ProfileField icon={MdLocationOn} label="Location" value={user.location} />
                  <ProfileField icon={MdCalendarToday} label="Date of Birth" value={user.dateOfBirth} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {isEditOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gradient-to-b from-sec-dark/80 to-pri-dark/80 rounded-2xl shadow-2xl backdrop-blur-xl border border-pri-white/10 w-full max-w-xl mx-auto overflow-hidden relative my-4"
              onClick={e => e.stopPropagation()}
            >
              {/* Glossy Effect Overlays */}
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-soft-pink/10" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/10 to-transparent" />
              </div>

              {/* Header */}
              <div className="relative flex justify-between items-center p-4 sm:p-6 border-b border-pri-white/10">
                <h2 className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-soft-pink to-pri-white">
                  Edit Profile
                </h2>
                <button 
                  onClick={() => setIsEditOpen(false)}
                  className="text-pri-gray hover:text-pri-white transition-colors"
                >
                  <IoIosCloseCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSaveChanges} className="relative p-4 sm:p-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="space-y-3 sm:space-y-4">
                    {/* Form Fields */}
                    {[
                      { label: 'Name', name: 'name', type: 'text' },
                      { label: 'Phone', name: 'mobile', type: 'tel' },
                      { label: 'Location', name: 'location', type: 'text' },
                      { label: 'Date of Birth', name: 'dateOfBirth', type: 'date' }
                    ].map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm text-soft-pink/70 mb-1">
                          {field.label}
                        </label>
                        <input
                          type={field.type}
                          name={field.name}
                          value={editedUser[field.name] || ''}
                          onChange={handleInputChange}
                          className="w-full bg-gradient-to-r from-soft-pink/15 to-soft-pink/20 border border-soft-pink/30 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-pri-dark focus:border-soft-pink focus:ring-1 focus:ring-soft-pink outline-none transition-colors backdrop-blur-md placeholder-pri-gray/50"
                        />
                      </div>
                    ))}

                    {/* Email Field (Read-only) */}
                    <div>
                      <label className="block text-sm text-soft-pink/70 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={editedUser.email || ''}
                        readOnly
                        className="w-full bg-gradient-to-r from-soft-pink/10 to-soft-pink/15 border border-soft-pink/20 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-pri-dark/80 outline-none transition-colors backdrop-blur-md cursor-not-allowed"
                      />
                    </div>

                    {/* Username Field with Check Availability Button */}
                    <div>
                      <label className="block text-sm text-soft-pink/70 mb-1">
                        Username
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          name="username"
                          value={editedUser.username || ''}
                          onChange={handleInputChange}
                          className={`flex-1 bg-gradient-to-r from-soft-pink/15 to-soft-pink/20 border ${
                            editedUser.username !== originalUsername 
                              ? (usernameStatus.available 
                                  ? 'border-green-400' 
                                  : 'border-red-400') 
                              : 'border-soft-pink/30'
                          } rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-pri-dark focus:border-soft-pink focus:ring-1 focus:ring-soft-pink outline-none transition-colors backdrop-blur-md placeholder-pri-gray/50`}
                          placeholder="Enter username without spaces"
                        />
                        <motion.button
                          type="button"
                          onClick={checkUsernameAvailability}
                          disabled={isCheckingUsername || !editedUser.username || editedUser.username === originalUsername}
                          className={`px-3 py-2 rounded-xl text-black text-sm font-medium transition-all ${
                            isCheckingUsername || !editedUser.username || editedUser.username === originalUsername
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-gradient-to-r from-soft-pink to-soft-pink/70 hover:shadow-md'
                          }`}
                          whileHover={!(isCheckingUsername || !editedUser.username || editedUser.username === originalUsername) ? { scale: 1.05 } : {}}
                          whileTap={!(isCheckingUsername || !editedUser.username || editedUser.username === originalUsername) ? { scale: 0.95 } : {}}
                        >
                          {isCheckingUsername ? "Checking..." : "Check Availability"}
                        </motion.button>
                      </div>
                      
                      {/* Username Status Message */}
                      {editedUser.username !== originalUsername && usernameStatus.message && (
                        <div className={`mt-1 text-sm flex items-center ${
                          usernameStatus.available ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {usernameStatus.available ? (
                            <FaCheck className="inline mr-1" />
                          ) : (
                            <FaTimes className="inline mr-1" />
                          )}
                          {usernameStatus.message}
                        </div>
                      )}
                    </div>

                    {/* Bio Field */}
                    <div>
                      <label className="block text-sm text-soft-pink/70 mb-1">Bio</label>
                      <textarea
                        name="bio"
                        rows="3"
                        value={editedUser.bio || ''}
                        onChange={handleInputChange}
                        className="w-full bg-gradient-to-r from-soft-pink/15 to-soft-pink/20 border border-soft-pink/30 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-pri-dark focus:border-soft-pink focus:ring-1 focus:ring-soft-pink outline-none transition-colors resize-none backdrop-blur-md placeholder-pri-gray/50"
                        placeholder="Tell us about yourself..."
                      />
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    className={`w-full ${
                      (editedUser.username !== originalUsername && (!usernameStatus.available || usernameStatus.needsValidation))
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-soft-pink to-soft-pink/70 hover:shadow-lg'
                    } text-pri-dark font-semibold py-2 sm:py-2.5 px-4 rounded-xl transition-all duration-300 mt-4 sm:mt-6 text-sm sm:text-base`}
                    whileHover={
                      !(editedUser.username !== originalUsername && (!usernameStatus.available || usernameStatus.needsValidation)) 
                        ? { scale: 1.02 } 
                        : {}
                    }
                    whileTap={
                      !(editedUser.username !== originalUsername && (!usernameStatus.available || usernameStatus.needsValidation)) 
                        ? { scale: 0.98 } 
                        : {}
                    }
                    disabled={editedUser.username !== originalUsername && (!usernameStatus.available || usernameStatus.needsValidation)}
                  >
                    Save Changes
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Edit Modal */}
      <AnimatePresence>
        {isPhotoPopupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gradient-to-b from-sec-dark to-pri-dark rounded-2xl shadow-2xl w-full max-w-xl mx-auto"
            >
              <EditUserPhoto
                setProfileImageUrl={handlePhotoChange}
                setShowEditPopup={setIsPhotoPopupOpen}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Profile;
